/* JOUBERT — la page collection (sections/joubert-collection.liquid).

   · Les cartes : les photos défilent au doigt, à la souris ou aux
     flèches, et bouclent sans revenir en arrière. Une copie de la
     dernière photo avant la première, une copie de la première après
     la dernière : on glisse sur la copie, puis on se recale d'un coup
     sur la vraie photo, sans que rien ne bouge à l'écran.
   · Les photos : la deuxième se charge quand la carte approche de
     l'écran, les autres au premier survol ou au premier geste.
   · La vue : 3, 4 ou 6 par rang sur ordinateur, 1 ou 2 sur téléphone,
     retenue dans le navigateur.
   · Le panneau Filtrer et trier : à chaque choix, la page filtrée est
     demandée à Shopify (Section Rendering API) pour afficher le nombre
     de créations ; « Voir » remplace la grille sans recharger. */
(() => {
  const doux = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Photos différées ---------- */
  const hydrate = (img) => {
    if (!img || !img.dataset.src) return;
    if (img.dataset.srcset) img.srcset = img.dataset.srcset;
    img.src = img.dataset.src;
    delete img.dataset.src;
    delete img.dataset.srcset;
  };
  const hydrateTout = (carte) => carte.querySelectorAll('img[data-src]').forEach(hydrate);

  const approche = 'IntersectionObserver' in window ? new IntersectionObserver((entrees) => {
    entrees.forEach((e) => {
      if (!e.isIntersecting) return;
      approche.unobserve(e.target);
      /* la deuxième photo, et sa copie de fin de bande si elle existe */
      const diapos = e.target.querySelectorAll('.jb-carte__diapo:not([aria-hidden]) img');
      hydrate(diapos[1]);
    });
  }, { rootMargin: '300px 0px' }) : null;

  const apresChargement = (f) => {
    const go = () => ('requestIdleCallback' in window ? requestIdleCallback(f, { timeout: 2000 }) : setTimeout(f, 200));
    if (document.readyState === 'complete') go(); else addEventListener('load', go, { once: true });
  };

  /* La première photo des cartes du bas de page : à l'approche de l'écran. */
  const proche = 'IntersectionObserver' in window ? new IntersectionObserver((entrees) => {
    entrees.forEach((e) => {
      if (!e.isIntersecting) return;
      proche.unobserve(e.target);
      /* la vraie première photo, pas la copie de boucle placée devant */
      hydrate(e.target.querySelector('.jb-carte__diapo:not([aria-hidden]) img'));
    });
  }, { rootMargin: '400px 0px' }) : null;

  /* ---------- Une carte ---------- */
  function monteCarte(carte) {
    if (carte.dataset.jbMonte) return;
    carte.dataset.jbMonte = '1';
    const premiere = carte.querySelector('.jb-carte__diapo img');
    if (premiere && premiere.dataset.src) { if (proche) proche.observe(carte); else hydrate(premiere); }
    const piste = carte.querySelector('[data-jb-piste]');
    const bande = piste.querySelector('.jb-carte__bande');
    const diapos = [...bande.children];
    const n = diapos.length;

    /* La photo est un lien : un simple appui ouvre la fiche ; après un
       glissé, on annule la navigation. */
    let bouge = 0;
    piste.addEventListener('click', (e) => {
      if (bouge > 8) { e.preventDefault(); bouge = 0; }
    });
    if (n < 2) return;

    /* les deuxièmes photos attendent la fin du chargement de la page,
       pour ne pas retarder les premières */
    apresChargement(() => { if (approche) approche.observe(carte); else hydrateTout(carte); });
    carte.addEventListener('pointerenter', () => hydrateTout(carte), { once: true });
    carte.addEventListener('focusin', () => hydrateTout(carte), { once: true });

    const points = [...carte.querySelectorAll('.jb-carte__points i')];
    const segments = [...carte.querySelectorAll('.jb-carte__progres span')];

    /* Les copies aux deux bouts : n + 2 cases, la photo i occupe la case i + 1. */
    const avant = diapos[n - 1].cloneNode(true);
    const apres = diapos[0].cloneNode(true);
    [avant, apres].forEach((c) => {
      c.setAttribute('aria-hidden', 'true');
      const img = c.querySelector('img');
      if (img) {
        img.alt = '';
        /* la copie attend qu'on glisse jusqu'à elle ; c'est le même
           fichier que l'original, déjà en cache à ce moment-là */
        img.loading = 'lazy';
        img.removeAttribute('fetchpriority');
      }
    });
    bande.insertBefore(avant, bande.firstChild);
    bande.appendChild(apres);
    const cases = n + 2;

    let i = 0;      /* la photo affichée, de 0 à n - 1 */
    let pos = 1;    /* la position de la bande, en photos */
    const toutes = [...bande.children];
    toutes.forEach((d) => { d.style.willChange = 'transform'; });
    const applique = () => {
      /* chaque photo à sa place, en largeurs de carte ; la photo affichée
         à 0 exactement, les autres écartées d'1 px pour qu'aucune ne
         déborde d'un pixel sur la photo affichée */
      toutes.forEach((d, k) => {
        const e = k - pos;
        d.style.transform = e === 0 ? 'none' : 'translate3d(calc(' + (e * 100) + '% ' + (e > 0 ? '+' : '-') + ' 1px), 0, 0)';
      });
      if (segments.length === 2) {
        /* p va de -1 (copie de la dernière) à n (copie de la première) ;
           le second segment prend le relais de l'autre côté. */
        const p = pos - 1;
        const relais = p > n - 1 ? p - n : p + n;
        segments[0].style.transform = 'translateX(' + (p * 100) + '%)';
        segments[1].style.transform = 'translateX(' + (relais * 100) + '%)';
      }
    };
    applique();
    const maj = () => points.forEach((p, k) => p.classList.toggle('actif', k === i));

    let anim = 0;
    const glisseVers = (cible, puis) => {
      cancelAnimationFrame(anim);
      const depart = pos, t0 = performance.now(), duree = doux() ? 0 : 380;
      const pas = (t) => {
        const k = duree ? Math.min(1, (t - t0) / duree) : 1;
        pos = depart + (cible - depart) * (1 - Math.pow(1 - k, 3));
        applique();
        if (k < 1) anim = requestAnimationFrame(pas); else if (puis) puis();
      };
      anim = requestAnimationFrame(pas);
    };
    const aller = (k) => {
      hydrateTout(carte);
      i = (k + n) % n;
      maj();
      glisseVers(k + 1, () => { pos = i + 1; applique(); });
    };
    carte.querySelectorAll('[data-jb-sens]').forEach((b) => b.addEventListener('click', (e) => {
      e.preventDefault();
      aller(i + Number(b.dataset.jbSens));
    }));

    /* Au doigt comme à la souris : la photo suit le geste. Au doigt, la
       page défile tant que le geste est plutôt vertical. */
    let x0 = null, y0 = 0, p0 = 0, sens = null, id = null;
    piste.addEventListener('pointerdown', (e) => {
      if (e.button) return;
      hydrateTout(carte);
      cancelAnimationFrame(anim);
      pos = i + 1; applique();
      x0 = e.clientX; y0 = e.clientY; p0 = pos; bouge = 0; id = e.pointerId;
      sens = e.pointerType === 'mouse' ? 'x' : null;
      if (sens === 'x') { piste.classList.add('saisie'); try { piste.setPointerCapture(id); } catch (err) { /* rien */ } }
    });
    piste.addEventListener('pointermove', (e) => {
      if (x0 === null || e.pointerId !== id) return;
      const dx = e.clientX - x0, dy = e.clientY - y0;
      bouge = Math.max(bouge, Math.hypot(dx, dy));
      if (sens === null && bouge > 8) {
        sens = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (sens === 'y') { x0 = null; return; }
        piste.classList.add('saisie');
        try { piste.setPointerCapture(id); } catch (err) { /* rien */ }
      }
      if (sens !== 'x') return;
      pos = Math.max(0, Math.min(cases - 1, p0 - dx / piste.getBoundingClientRect().width));
      applique();
    });
    const lache = (e) => {
      if (x0 === null || e.pointerId !== id) return;
      const dx = x0 - e.clientX;
      x0 = null;
      piste.classList.remove('saisie');
      if (sens === 'x') aller(Math.abs(dx) > 40 ? i + (dx > 0 ? 1 : -1) : i);
    };
    piste.addEventListener('pointerup', lache);
    piste.addEventListener('pointercancel', lache);
    /* le navigateur ne doit pas tirer l'image comme un fichier */
    piste.addEventListener('dragstart', (e) => e.preventDefault());
  }

  /* ---------- Une page collection ---------- */
  function monte(racine) {
    if (!racine || racine.dataset.jbMonte) return;
    racine.dataset.jbMonte = '1';
    const section = racine.dataset.section;
    const resultats = racine.querySelector('[data-jb-resultats]');
    const grille = () => racine.querySelector('[data-jb-grille]');

    const monteGrille = () => {
      const g = grille();
      if (!g) return;
      g.querySelectorAll('[data-jb-carte]').forEach(monteCarte);
      appliqueVue();
    };

    /* ---------- La vue ---------- */
    const lire = (cle) => { try { return localStorage.getItem(cle); } catch (e) { return null; } };
    const ecrire = (cle, v) => { try { localStorage.setItem(cle, v); } catch (e) { /* stockage indisponible */ } };
    const appliqueVue = () => {
      const g = grille();
      const c = lire('jb-vue-collection');
      const t = lire('jb-vue-collection-tel');
      const colonnes = c === '3' || c === '6' ? c : '4';
      const parTel = t === '1' ? '1' : '2';
      racine.querySelectorAll('[data-colonnes]:not([data-jb-grille])').forEach((b) => b.setAttribute('aria-pressed', b.dataset.colonnes === colonnes));
      racine.querySelectorAll('[data-tel]:not([data-jb-grille])').forEach((b) => b.setAttribute('aria-pressed', b.dataset.tel === parTel));
      if (!g) return;
      g.dataset.colonnes = colonnes;
      g.dataset.tel = parTel;
      /* la taille réelle des photos, pour que le navigateur choisisse le bon fichier */
      const sizes = '(min-width: 901px) ' + Math.ceil(100 / Number(colonnes)) + 'vw, ' + (parTel === '1' ? 100 : 50) + 'vw';
      g.querySelectorAll('.jb-carte__img').forEach((img) => { if (img.getAttribute('sizes') !== sizes) img.setAttribute('sizes', sizes); });
    };
    racine.querySelectorAll('.jb-coll__vue').forEach((b) => b.addEventListener('click', () => {
      if (b.dataset.colonnes) ecrire('jb-vue-collection', b.dataset.colonnes);
      if (b.dataset.tel) ecrire('jb-vue-collection-tel', b.dataset.tel);
      appliqueVue();
    }));

    monteGrille();

    /* Le thème fait redescendre son en-tête caché dès que la souris passe
       dans les 100 px du haut de la fenêtre. Page défilée, la barre est
       alors tout en haut : en allant cliquer « Filtrer et trier », la
       souris faisait redescendre l'en-tête, qui recouvrait la barre et la
       poussait vers le bas. Au-dessus de la collection, seul le défilement
       vers le haut fait revenir l'en-tête. */
    racine.addEventListener('pointermove', (e) => { if (e.clientY < 100) e.stopPropagation(); });

    /* ---------- Le panneau ---------- */
    const dialogue = racine.querySelector('[data-jb-filtres]');
    const formulaire = racine.querySelector('[data-jb-formulaire]');
    const ouvre = racine.querySelector('[data-jb-ouvre-filtres]');
    const voir = racine.querySelector('[data-jb-voir]');
    if (!dialogue || !formulaire || typeof dialogue.showModal !== 'function') return;

    let minuteur = 0;
    const ouvrir = () => {
      clearTimeout(minuteur);
      if (!dialogue.open) dialogue.showModal();
      dialogue.scrollLeft = 0;
      dialogue.scrollTop = 0;
      /* la page ne défile plus derrière ; la place de la barre de
         défilement est gardée, pour que rien ne se décale */
      document.documentElement.style.scrollbarGutter = 'stable';
      document.documentElement.style.overflow = 'hidden';
      requestAnimationFrame(() => requestAnimationFrame(() => dialogue.classList.add('ouvert')));
    };
    const fermer = () => {
      dialogue.classList.remove('ouvert');
      document.documentElement.style.overflow = '';
      document.documentElement.style.scrollbarGutter = '';
      minuteur = setTimeout(() => { if (dialogue.open) dialogue.close(); ouvre.focus({ preventScroll: true }); }, doux() ? 0 : 450);
    };
    ouvre.addEventListener('click', ouvrir);
    dialogue.addEventListener('click', (e) => { if (e.target.closest('[data-jb-ferme]')) fermer(); });
    dialogue.addEventListener('cancel', (e) => { e.preventDefault(); fermer(); });

    /* L'adresse de la page pour les choix du formulaire. */
    const adresse = () => {
      const p = new URLSearchParams();
      new FormData(formulaire).forEach((v, k) => {
        if (k === 'sort_by' && v === racine.dataset.triDefaut) return;
        p.append(k, v);
      });
      const q = p.toString();
      return racine.dataset.url + (q ? '?' + q : '');
    };

    const cache = new Map();
    const demande = (url) => {
      if (!cache.has(url)) {
        const u = url + (url.includes('?') ? '&' : '?') + 'section_id=' + encodeURIComponent(section);
        cache.set(url, fetch(u, { credentials: 'same-origin' })
          .then((r) => { if (!r.ok) throw new Error(r.status); return r.text(); })
          .then((html) => new DOMParser().parseFromString(html, 'text/html'))
          .catch((err) => { cache.delete(url); throw err; }));
      }
      return cache.get(url);
    };

    /* À chaque choix : le nombre de créations et les compteurs des puces. */
    let dernier = '';
    const previsualise = () => {
      const url = adresse();
      dernier = url;
      voir.setAttribute('aria-busy', 'true');
      demande(url).then((doc) => {
        if (url !== dernier) return;
        voir.removeAttribute('aria-busy');
        const neuf = doc.querySelector('[data-jb-voir]');
        if (neuf) voir.textContent = neuf.textContent;
        formulaire.querySelectorAll('.jb-puce input').forEach((input) => {
          const autre = [...doc.querySelectorAll('.jb-puce input')].find((x) => x.name === input.name && x.value === input.value);
          const nb = input.parentNode.querySelector('[data-jb-nb]');
          if (autre) {
            input.disabled = false;
            if (nb) nb.textContent = autre.parentNode.querySelector('[data-jb-nb]').textContent;
          } else if (!input.checked) {
            input.disabled = true;
            if (nb) nb.textContent = '0';
          }
        });
      }).catch(() => voir.removeAttribute('aria-busy'));
    };
    formulaire.addEventListener('change', previsualise);
    racine.querySelector('[data-jb-efface]').addEventListener('click', () => {
      formulaire.querySelectorAll('.jb-puce input').forEach((x) => { x.checked = false; });
      const defaut = formulaire.querySelector('input[name="sort_by"][value="' + racine.dataset.triDefaut + '"]');
      if (defaut) defaut.checked = true;
      previsualise();
    });

    /* « Voir » : la grille, le nombre et la pastille de la page filtrée,
       sans recharger ; l'adresse suit, pour le retour et le partage. */
    const remplace = (doc, url) => {
      const r = doc.querySelector('[data-jb-resultats]');
      if (!r) throw new Error('réponse sans résultats');
      resultats.innerHTML = r.innerHTML;
      ['[data-jb-compte]', '[data-jb-pastille]'].forEach((s) => {
        const a = racine.querySelector(s), b = doc.querySelector(s);
        if (a && b) { a.textContent = b.textContent; a.hidden = b.hidden; }
      });
      history.pushState({ jbCollection: true }, '', url);
      pousse = true;
      monteGrille();
    };
    formulaire.addEventListener('submit', (e) => {
      e.preventDefault();
      const url = adresse();
      resultats.classList.add('charge');
      demande(url).then((doc) => {
        remplace(doc, url);
        resultats.classList.remove('charge');
        fermer();
        const barre = racine.querySelector('.jb-coll__barre');
        if (barre && barre.getBoundingClientRect().top < 0) barre.scrollIntoView({ block: 'start' });
      }).catch(() => { location.href = url; });
    });
    /* Retour arrière après un filtrage : on recharge la page de l'adresse. */
    let pousse = false;
    addEventListener('popstate', () => { if (pousse) location.reload(); });
  }

  const tout = () => {
    document.querySelectorAll('[data-jb-coll]').forEach(monte);
    // cartes seules (page de résultats de recherche)
    document.querySelectorAll('[data-jb-cartes-seules] [data-jb-carte]').forEach(monteCarte);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', tout); else tout();
  document.addEventListener('shopify:section:load', (e) => monte(e.target.querySelector('[data-jb-coll]')));
})();
